const DEFAULT_CONTEXT = {
    id: 1,
};

var Scope;
(function (Scope) {
    Scope[Scope["DEFAULT"] = 0] = "DEFAULT";
    Scope[Scope["TRANSIENT"] = 1] = "TRANSIENT";
})(Scope || (Scope = {}));

function createInstanceHolder(options) {
    return Object.assign({}, options);
}

var InstanceWrapperType;
(function (InstanceWrapperType) {
    InstanceWrapperType[InstanceWrapperType["CLASS"] = 0] = "CLASS";
    InstanceWrapperType[InstanceWrapperType["VALUE"] = 1] = "VALUE";
    InstanceWrapperType[InstanceWrapperType["FACTORY"] = 2] = "FACTORY";
})(InstanceWrapperType || (InstanceWrapperType = {}));
class InstanceWrapper {
    constructor(definition) {
        this.values = new WeakMap();
        const { host, name, type, ctor, instance, factory, scope } = definition;
        this.host = host;
        this.name = name;
        this.type = type;
        this.ctor = ctor;
        this.instance = instance;
        this.factory = factory;
        this.scope = scope;
    }
    get instance() {
        return this.getInstanceByContext(DEFAULT_CONTEXT).instance;
    }
    set instance(value) {
        this.setInstanceByContext(DEFAULT_CONTEXT, value);
    }
    safeGetHolder(context) {
        let holder = this.values.get(context);
        if (!holder) {
            holder = createInstanceHolder({});
            this.values.set(context, holder);
        }
        return holder;
    }
    getInstanceByContext(context) {
        return this.safeGetHolder(context);
    }
    setInstanceByContext(context, value) {
        const holder = this.safeGetHolder(context);
        holder.instance = value;
    }
    isClass() {
        return this.type === InstanceWrapperType.CLASS;
    }
    isValue() {
        return this.type === InstanceWrapperType.VALUE;
    }
    isFactory() {
        return this.type === InstanceWrapperType.FACTORY;
    }
}

function isEmpty(value) {
    return value == null;
}
function isUndefined(value) {
    return typeof value === 'undefined';
}
function isFunction(value) {
    return typeof value === 'function';
}
function isPromise(value) {
    return value instanceof Promise;
}

class AbstractPowerVesselize {
    constructor(options) {
        this.vesselize = new Vesselize(options);
    }
    getProvider(provide) {
        return this.vesselize.getProvider(provide);
    }
    get(provide) {
        return this.vesselize.get(provide);
    }
    getAsync(provide) {
        return this.vesselize.getAsync(provide);
    }
    getInContext(provide, context) {
        return this.vesselize.getInContext(provide, context);
    }
    getInContextAsync(provide, context) {
        return this.vesselize.getInContextAsync(provide, context);
    }
}
class Vesselize {
    constructor(options) {
        this.wrappers = new Map();
        this.options = options;
        this.initialzie();
    }
    initialzie() {
        const options = this.options;
        this.addProviders(options.providers);
        this.createInstances();
        this.invokeAwares();
    }
    addProviders(providers = []) {
        for (const provider of providers) {
            this.addProvider(provider);
        }
    }
    addProvider(provider) {
        if (this.isCustomProvider(provider)) {
            return this.addCustomProvider(provider);
        }
        const name = this.getProviderName(provider);
        this.addClassProvider({
            name,
            token: provider,
            useClass: provider,
        });
    }
    addCustomProvider(provider) {
        const name = this.getProviderName(provider.token);
        provider = { ...provider, name };
        if (this.isClassProvider(provider)) {
            this.addClassProvider(provider);
        }
        else if (this.isValueProvider(provider)) {
            this.addValueProvider(provider);
        }
        else if (this.isFactoryProvider(provider)) {
            this.addFactoryProvider(provider);
        }
    }
    addClassProvider(provider) {
        const { name, useClass: ctor, scope = Scope.DEFAULT } = provider;
        this.wrappers.set(name, new InstanceWrapper({
            name,
            ctor,
            scope,
            host: this,
            type: InstanceWrapperType.CLASS,
        }));
    }
    addValueProvider(provider) {
        const { name, useValue: value } = provider;
        this.wrappers.set(name, new InstanceWrapper({
            name,
            instance: value,
            host: this,
            type: InstanceWrapperType.VALUE,
        }));
    }
    addFactoryProvider(provider) {
        const { name, useFactory: factory } = provider;
        this.wrappers.set(name, new InstanceWrapper({
            name,
            factory,
            host: this,
            type: InstanceWrapperType.FACTORY,
        }));
    }
    isCustomProvider(provider) {
        return !isUndefined(provider.token);
    }
    isClassProvider(provider) {
        return !isUndefined(provider.useClass);
    }
    isValueProvider(provider) {
        return !isUndefined(provider.useValue);
    }
    isFactoryProvider(provider) {
        return !isUndefined(provider.useFactory);
    }
    getProviderName(provide) {
        return isFunction(provide)
            ? provide.name
            : provide;
    }
    createInstances() {
        const wrappers = this.wrappers.values();
        for (const wrapper of wrappers) {
            this.createInstance(wrapper);
        }
    }
    async createInstance(wrapper, context = DEFAULT_CONTEXT) {
        if (wrapper.isClass()) {
            this.createClassInstance(wrapper, context);
        }
        else if (wrapper.isFactory()) {
            await this.createFactoryInstance(wrapper, context);
        }
    }
    createClassInstance(wrapper, context = DEFAULT_CONTEXT) {
        const { ctor: Ctor } = wrapper;
        const instance = new Ctor();
        wrapper.setInstanceByContext(context, instance);
    }
    async createFactoryInstance(wrapper, context = DEFAULT_CONTEXT) {
        const { factory } = wrapper;
        const holder = wrapper.getInstanceByContext(context);
        const result = factory();
        let donePromise;
        if (isPromise(result)) {
            holder.async = true;
            holder.isResolved = false;
            holder.promise = result;
            donePromise = result.then((instance) => {
                holder.instance = instance;
                holder.isResolved = true;
                return instance;
            });
        }
        else {
            holder.instance = result;
            donePromise = Promise.resolve(result);
        }
        return donePromise.then((instance) => {
            wrapper.setInstanceByContext(context, instance);
        });
    }
    invokeAwares() {
        const wrappers = this.wrappers.values();
        for (const wrapper of wrappers) {
            const holder = wrapper.getInstanceByContext(DEFAULT_CONTEXT);
            this.invokeInstanceAware(holder);
        }
    }
    async invokeInstanceAware(holder) {
        if (holder.async) {
            await holder.promise.then(() => {
                this.invokeVesselizeAware(holder.instance);
            });
        }
        else {
            this.invokeVesselizeAware(holder.instance);
        }
    }
    invokeVesselizeAware(instance) {
        if (this.isVesselizeAware(instance)) {
            instance.setVesselize(this);
        }
    }
    isVesselizeAware(instance) {
        return !isUndefined(instance.setVesselize);
    }
    getProvider(provide) {
        const name = this.getProviderName(provide);
        const wrapper = this.wrappers.get(name);
        if (!isEmpty(wrapper)) {
            if (wrapper.isClass()) {
                return wrapper.ctor;
            }
            else if (wrapper.isFactory()) {
                return wrapper.factory;
            }
            else if (wrapper.isValue()) {
                return wrapper.instance;
            }
        }
    }
    get(provide) {
        const name = this.getProviderName(provide);
        const wrapper = this.wrappers.get(name);
        if (!isEmpty(wrapper)) {
            return wrapper.instance;
        }
    }
    async createInstanceInContext(provide, context) {
        const name = this.getProviderName(provide);
        const wrapper = this.wrappers.get(name);
        if (!isEmpty(wrapper)) {
            await this.createInstance(wrapper, context);
            await this.invokeInstanceAware(wrapper.getInstanceByContext(context));
        }
    }
    getInContext(provide, context) {
        const name = this.getProviderName(provide);
        const wrapper = this.wrappers.get(name);
        if (!isEmpty(wrapper)) {
            const holder = wrapper.getInstanceByContext(context);
            if (!holder.instance) {
                this.createInstanceInContext(provide, context);
            }
            return holder.instance;
        }
    }
    async getAsyncInstance(provide, context = DEFAULT_CONTEXT) {
        const name = this.getProviderName(provide);
        const wrapper = this.wrappers.get(name);
        if (!isEmpty(wrapper)) {
            const holder = wrapper.getInstanceByContext(context);
            if (!holder.async || holder.isResolved) {
                return Promise.resolve(holder.instance);
            }
            else {
                return holder.promise;
            }
        }
    }
    async getAsync(provide) {
        const name = this.getProviderName(provide);
        return this.getAsyncInstance(name);
    }
    async getInContextAsync(provide, context) {
        const name = this.getProviderName(provide);
        const wrapper = this.wrappers.get(name);
        if (!isEmpty(wrapper)) {
            const holder = wrapper.getInstanceByContext(context);
            if (isEmpty(holder.instance) || isEmpty(holder.promise)) {
                await this.createInstanceInContext(provide, context);
            }
        }
        return this.getAsyncInstance(name, context);
    }
}

export { AbstractPowerVesselize, DEFAULT_CONTEXT, Scope, Vesselize };
